// users-delegation: מסך "משתמשים" (admin ובעל "הרשאת האצלה"). הלקוח רק מציג; כל הגבלה נאכפת בשרת והודעת השרת מוצגת כמו שהיא.
import { z } from 'zod';
import { MfaPolicy, Role } from '@aibify/core';
import { ApiError, authed, json } from './api.ts';
import { getAccessToken } from './auth.ts';
import { T } from './strings.ts';

export const UsersMe = z.object({
  isAdmin: z.boolean(),
  isDelegate: z.boolean(),
  canManageUsers: z.boolean(),
  delegateKilled: z.boolean(),
  // data-access-rls: מוגבל → פס "מוצגים רק הנתונים שהוקצו לך"
  dataAccess: z.object({ mode: z.enum(['all', 'restricted']) }).optional(),
});
export type UsersMe = z.infer<typeof UsersMe>;

export const UserRow = z.object({
  id: z.string(),
  email: z.string(),
  nameHe: z.string(),
  role: Role,
  active: z.boolean(),
  createdBy: z.object({ id: z.string(), nameHe: z.string() }).nullable(),
  delegate: z.boolean().optional(),
  editable: z.boolean(),
});
export type UserRow = z.infer<typeof UserRow>;

const Hosts = z.union([z.literal('*'), z.array(z.string())]).nullable();
export const UserAccess = z.object({
  user: z.object({ id: z.string(), email: z.string(), nameHe: z.string(), role: Role, active: z.boolean() }),
  editable: z.boolean(),
  asDelegate: z.boolean(),
  delegate: z.boolean(),
  interfaces: z.array(z.object({ id: z.string(), nameHe: z.string(), granted: z.boolean(), grantable: z.boolean() })),
  scopes: z.array(z.object({ id: z.string(), hosts: Hosts, until: z.string().nullable(), purposeHe: z.string() })),
  myScopes: z.array(z.object({ hosts: Hosts, until: z.string().nullable() })).nullable(),
  roles: z.array(Role),
  shares: z.array(z.object({ slug: z.string(), nameHe: z.string(), sharedBy: z.object({ id: z.string(), nameHe: z.string() }).nullable() })),
  // platform-mfa-totp: יש גורם מאומת → admin רואה "איפוס אפליקציית אימות"
  mfa: z.object({ enrolled: z.boolean() }).optional(),
});
export type UserAccess = z.infer<typeof UserAccess>;

export const MfaPolicyRes = MfaPolicy.extend({ requiredAt: z.string().nullable() });
export type MfaPolicyRes = z.infer<typeof MfaPolicyRes>;

export const LogEntry = z.object({
  id: z.string(),
  at: z.string(),
  actor: z.object({ id: z.string().nullable(), nameHe: z.string(), role: z.string(), delegate: z.boolean() }),
  via: z.string(),
  target: z.object({ id: z.string().nullable(), nameHe: z.string(), email: z.string() }),
  action: z.string(),
  textHe: z.string(),
});
export type LogEntry = z.infer<typeof LogEntry>;

// data-access-rls: "גישה לנתונים" במגירת המשתמש. הקטלוג והערכים — רק מה שהצופה עצמו רואה; התקרה נאכפת בשרת (exceeds_grantor).
export const DaCatalogEntity = z.object({
  world: z.string(),
  entity: z.string(),
  nameHe: z.string(),
  dimension: z.object({ key: z.string(), nameHe: z.string() }).nullable(),
  columns: z.array(z.object({ key: z.string(), nameHe: z.string() })),
});
export type DaCatalogEntity = z.infer<typeof DaCatalogEntity>;
export const DaValues = z.object({
  dimension: z.string(),
  values: z.array(z.object({ value: z.string(), label: z.string().nullable().optional(), rows: z.number() })),
  truncated: z.boolean(),
});
export type DaValues = z.infer<typeof DaValues>;
const DaEntityRef = z.object({ world: z.string(), entity: z.string() });
export const DaBody = z.object({
  mode: z.enum(['all', 'restricted']),
  rows: z.array(DaEntityRef.extend({ dimension: z.string().optional(), values: z.array(z.string()) })),
  denyEntities: z.array(DaEntityRef),
  denyColumns: z.array(DaEntityRef.extend({ column: z.string() })),
});
export type DaBody = z.infer<typeof DaBody>;
export const DaView = DaBody.extend({
  user: z.object({ id: z.string(), nameHe: z.string(), role: z.string() }),
  restrictable: z.boolean(),
  editable: z.boolean().optional(),
  asDelegate: z.boolean().optional(),
});
export type DaView = z.infer<typeof DaView>;
export const DaPreview = z.object({
  entities: z.array(z.object({ worldKey: z.string(), entity: z.string(), nameHe: z.string(), status: z.enum(['all', 'rows', 'blocked']), rows: z.number().nullable(), blockedColumns: z.array(z.string()) })),
  spreads: z.array(z.object({ key: z.string(), nameHe: z.string(), access: z.enum(['ok', 'blocked_columns', 'blocked_entities']) })),
});
export type DaPreview = z.infer<typeof DaPreview>;
// §7.1 חוויית המאציל: החלוקות בקטלוג (לפי העץ), "מה הוא יראה" לבחירה שלא נשמרה, היסטוריה וביטול.
export const DaDimension = z.object({ id: z.string(), world: z.string(), key: z.string(), nameHe: z.string(), entities: z.array(z.object({ entity: z.string(), nameHe: z.string() })) });
export type DaDimension = z.infer<typeof DaDimension>;
export const DaCatalog = z.object({ entities: z.array(DaCatalogEntity), dimensions: z.array(DaDimension).default([]) });
export type DaCatalog = z.infer<typeof DaCatalog>;
export const DaDraftPreview = z.object({
  entities: z.array(z.object({ world: z.string(), entity: z.string(), nameHe: z.string(), rows: z.number().nullable(), blocked: z.boolean(), hiddenColumns: z.array(z.string()) })),
  summaryHe: z.string(),
});
export type DaDraftPreview = z.infer<typeof DaDraftPreview>;
export const DaHistory = z.object({ entries: z.array(z.object({ id: z.string(), at: z.string(), actorNameHe: z.string(), textHe: z.string(), canUndo: z.boolean() })) });
export type DaHistoryEntry = z.infer<typeof DaHistory>['entries'][number];

/**
 * קריאה עם הפירוט של השרת (details.detailHe, למשל של exceeds_grantor): json() מציג רק textHe, וכאן שגיאה לעולם לא שקטה.
 * 401 → json() הרגיל (רענון).
 */
async function detailed<S extends z.ZodTypeAny>(schema: S, path: string, init: RequestInit): Promise<z.infer<S>> {
  const token = await getAccessToken().catch(() => null);
  const r = token ? await fetch(path, { ...init, headers: { ...(init.headers as Record<string, string>), authorization: `Bearer ${token}` } }).catch(() => null) : null;
  if (!r || r.status === 401) return json(schema, path, init);
  const data: unknown = await r.json().catch(() => null);
  if (!r.ok) {
    const e = (data as { error?: { code?: string; textHe?: string; details?: { detailHe?: string } } } | null)?.error;
    if (e?.code && e.textHe) throw new ApiError(r.status, e.code, e.details?.detailHe ? `${e.textHe}. ${e.details.detailHe}` : e.textHe);
    throw new ApiError(r.status, 'server', T.serverError(r.status));
  }
  const v = schema.safeParse(data);
  if (!v.success) throw new ApiError(r.status, 'bad_response', T.badResponse);
  return v.data;
}

const Secret = z.object({ labelHe: z.string(), value: z.string() });
const Ok = z.object({}).passthrough();
const enc = encodeURIComponent;
const send = (method: string, body?: unknown): RequestInit => ({ method, headers: { 'content-type': 'application/json' }, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

export const usersApi = {
  me: () => json(UsersMe, '/api/users/me'),
  list: () => json(z.object({ users: z.array(UserRow) }), '/api/users').then((r) => r.users),
  access: (id: string) => json(UserAccess, `/api/users/${enc(id)}/access`),
  log: (q: { target?: string } = {}) => json(z.object({ entries: z.array(LogEntry) }), `/api/users/log?limit=100${q.target ? `&target=${enc(q.target)}` : ''}`).then((r) => r.entries),
  shareables: () => json(z.object({ artifacts: z.array(z.object({ slug: z.string(), nameHe: z.string(), own: z.boolean() }).passthrough()) }), '/api/users/shareables').then((r) => r.artifacts),
  create: (b: { email: string; nameHe: string; role: Role }) => json(z.object({ user: UserAccess.shape.user, secret: Secret }), '/api/users', send('POST', b)),
  setRole: (id: string, role: Role) => json(Ok, `/api/users/${enc(id)}`, send('PATCH', { role })),
  setActive: (id: string, active: boolean) => json(Ok, `/api/users/${enc(id)}/${active ? 'reactivate' : 'deactivate'}`, send('POST', {})),
  setGrant: (id: string, iface: string, on: boolean) => json(Ok, `/api/users/${enc(id)}/grants/${enc(iface)}`, on ? send('PUT', {}) : send('DELETE')),
  addScope: (id: string, b: { hosts: string[] | '*'; until: string; purposeHe?: string }) => json(Ok, `/api/users/${enc(id)}/scopes`, send('POST', b)),
  removeScope: (id: string, scopeId: string) => json(Ok, `/api/users/${enc(id)}/scopes/${enc(scopeId)}`, send('DELETE')),
  loginLink: (id: string) => json(z.object({ url: z.string(), expiresAt: z.string() }), `/api/users/${enc(id)}/login-link`, send('POST', {})),
  share: (id: string, slug: string) => json(Ok, `/api/users/${enc(id)}/shares`, send('POST', { slug })),
  unshare: (id: string, slug: string) => json(Ok, `/api/users/${enc(id)}/shares/${enc(slug)}`, send('DELETE')),
  setDelegation: (id: string, on: boolean) => json(Ok, `/api/users/${enc(id)}/delegation`, send('POST', { on })),
  // platform-mfa-totp (admin): איפוס גורם למשתמש; מדיניות המופע
  mfaReset: (id: string) => json(z.object({ removed: z.number() }), `/api/users/${enc(id)}/mfa/reset`, send('POST', {})),
  mfaPolicy: () => json(MfaPolicyRes, '/api/instance/mfa-policy'),
  setMfaPolicy: (p: MfaPolicy) => json(MfaPolicyRes, '/api/instance/mfa-policy', send('PUT', p)),
  daCatalog: () => json(DaCatalog, '/api/users/data-access/catalog'),
  daValues: (world: string, entity: string) => json(DaValues, `/api/users/data-access/values?world=${enc(world)}&entity=${enc(entity)}`),
  dataAccess: (id: string) => json(DaView, `/api/users/${enc(id)}/data-access`),
  setDataAccess: (id: string, body: DaBody) => detailed(DaView, `/api/users/${enc(id)}/data-access`, send('PUT', body)),
  daPreviewDraft: (access: DaBody, userId?: string) => detailed(DaDraftPreview, '/api/users/data-access/preview-draft', send('POST', { ...(userId ? { userId } : {}), access })),
  daHistory: (id: string) => json(DaHistory, `/api/users/${enc(id)}/data-access/history`).then((r) => r.entries),
  daUndo: (id: string, changeId: string) => detailed(DaView, `/api/users/${enc(id)}/data-access/undo`, send('POST', { changeId })),
  daPreview: (id: string) => json(DaPreview, `/api/users/${enc(id)}/data-access/preview`),
  daCopy: (id: string) => json(DaBody, `/api/users/${enc(id)}/data-access/copy`),
};

/** ניווט בלבד: האם להציג "משתמשים". שגיאה = לא מציגים (האכיפה בשרת). */
export async function canManageUsers(): Promise<boolean> {
  try {
    const r = await authed('/api/users/me');
    const m = UsersMe.safeParse(await r.json());
    return m.success && m.data.canManageUsers;
  } catch {
    return false;
  }
}
